import { Link } from "react-router-dom";
import { formatDateHuman, type MatterRow } from "@themislex/shared";
import { useApi } from "../../api/hooks";
import { useAuth } from "../../auth/AuthContext";
import {
  Card,
  EmptyState,
  ErrorNote,
  MatterStatusBadge,
  Money,
  PageHeader,
  Spinner,
} from "../../components/ui";

export default function PortalHome() {
  const { user, clientSelf } = useAuth();
  const { data, error, loading } = useApi<{ matters: MatterRow[] }>("/api/matters");

  return (
    <>
      <PageHeader
        title={`Welcome, ${user?.name ?? ""}`}
        subtitle="A summary of your matters with the chambers."
      />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            Your matters
          </p>
          <p className="mt-2 font-heading text-2xl font-bold">
            {clientSelf?.matterCount ?? "—"}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            Invoices awaiting payment
          </p>
          <p className="mt-2 font-heading text-2xl font-bold">
            {clientSelf?.openInvoiceCount ?? "—"}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            Balance payable
          </p>
          <p className="mt-2 font-heading text-2xl font-bold">
            {clientSelf ? <Money minor={clientSelf.balanceMinor} /> : "—"}
          </p>
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {data &&
        (data.matters.length === 0 ? (
          <Card>
            <EmptyState
              title="No matters on record yet."
              hint="When the chambers opens a matter for you, it will appear here."
            />
          </Card>
        ) : (
          <Card title="Your matters">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Title</th>
                    <th scope="col">Case type</th>
                    <th scope="col">Case number</th>
                    <th scope="col">Court / forum</th>
                    <th scope="col">Next hearing</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matters.map((matter) => (
                    <tr key={matter.id}>
                      <td className="font-medium">
                        <Link
                          to={`/portal/matters/${matter.id}`}
                          className="text-accent-text hover:underline"
                        >
                          {matter.title}
                        </Link>
                      </td>
                      <td>{matter.caseType}</td>
                      <td>{matter.caseNumber ?? "—"}</td>
                      <td>{matter.court ?? "—"}</td>
                      <td className="whitespace-nowrap">
                        {formatDateHuman(matter.nextHearingDate)}
                      </td>
                      <td>
                        <MatterStatusBadge status={matter.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-muted">
              Matters are maintained by the chambers. For any queries, please
              contact your advocate.
            </p>
          </Card>
        ))}
    </>
  );
}
